import { Component, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, LogOut, Compass } from "lucide-react";
import { logger } from "@/lib/logger";
import { SUPPORT_INFO } from "@/constants/messages";

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * PageErrorBoundaryLocal - Error Boundary para páginas específicas
 * 
 * 🛡️ RESILIENCE: Previne que erros em uma página quebrem toda a aplicação
 * 
 * Diferença do global ErrorBoundary:
 * - Global: Captura erros não tratados em toda a app
 * - Local: Captura erros apenas na página/seção específica
 */
export class PageErrorBoundaryLocal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('🔴 [PageErrorBoundaryLocal] Error caught:', error, errorInfo.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  handleLogoutAndHome = async () => {
    // Dynamically import to avoid circular dependencies
    const { useStudentAuthStore } = await import("@/stores/studentAuthStore");
    const store = useStudentAuthStore.getState();
    
    if (store.isStudentLoggedIn) {
      await store.logoutStudent();
    }
    
    window.location.href = '/';
  };

  handleGoToDiscover = () => {
    // Navigate to a public route that won't redirect back
    window.location.href = '/descobrir-experiencias';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-background border border-destructive/20 rounded-lg p-8 text-center">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="font-rubik text-2xl font-bold mb-2">
              ops! tivemos um tropeço
            </h2>
            <p className="text-muted-foreground mb-6 font-lexend">
              {this.props.fallbackMessage || "algo não saiu como esperado. vamos tentar de novo?"}
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="text-xs text-left bg-muted p-4 rounded mb-4 overflow-auto max-h-32">
                {this.state.error.toString()}
              </pre>
            )}
            <div className="flex flex-col gap-3">
              <Button onClick={this.handleReset} variant="default" className="w-full">
                Tentar Novamente
              </Button>
              <div className="flex gap-2">
                <Button 
                  onClick={this.handleGoToDiscover} 
                  variant="outline"
                  className="flex-1"
                >
                  <Compass className="h-4 w-4 mr-2" />
                  Descobrir
                </Button>
                <Button 
                  onClick={this.handleLogoutAndHome} 
                  variant="ghost"
                  className="flex-1"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sair
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                {SUPPORT_INFO.message}{' '}
                <a href={SUPPORT_INFO.mailto} className="underline hover:text-foreground transition-colors">
                  {SUPPORT_INFO.email}
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
